import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { usePipelineOptions } from "@/lib/pipelineOptions";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Layers, Plus, Trash2, Lock, GripVertical, Save, X, Pencil } from "lucide-react";

const TABS = [
  { key: "status", label: "Statuses" },
  { key: "plan", label: "Plans" },
  { key: "service_group", label: "Service Groups" },
];

export function PipelineFiltersCard() {
  const { rawStatus, rawPlan, rawServiceGroup, refresh } = usePipelineOptions();
  const [tab, setTab] = useState("status");
  const [saving, setSaving] = useState(false);
  const [draggingSlug, setDraggingSlug] = useState(null);

  const items = useMemo(() => {
    const src = tab === "status" ? rawStatus : tab === "plan" ? rawPlan : rawServiceGroup;
    return [...src].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tab, rawStatus, rawPlan, rawServiceGroup]);

  const handleAdd = async (label) => {
    if (!label.trim()) return false;
    setSaving(true);
    try {
      await api.post(`/admin/pipeline-options/${tab}`, { label: label.trim() });
      toast.success(`Added "${label.trim()}"`);
      await refresh();
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (slug, label) => {
    if (!label.trim()) return false;
    setSaving(true);
    try {
      await api.patch(`/admin/pipeline-options/${tab}/${encodeURIComponent(slug)}`, { label: label.trim() });
      toast.success("Renamed");
      await refresh();
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to rename");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug, label) => {
    if (!window.confirm(`Delete "${label}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/pipeline-options/${tab}/${encodeURIComponent(slug)}`);
      toast.success(`Deleted "${label}"`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = async (fromSlug, toSlug) => {
    if (!fromSlug || fromSlug === toSlug) return;
    const slugs = items.map((i) => i.slug);
    const fromIdx = slugs.indexOf(fromSlug);
    const toIdx = slugs.indexOf(toSlug);
    if (fromIdx < 0 || toIdx < 0) return;
    slugs.splice(fromIdx, 1);
    slugs.splice(toIdx, 0, fromSlug);
    setSaving(true);
    try {
      await api.post(`/admin/pipeline-options/${tab}/reorder`, { slugs });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reorder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mt-6 bg-white border border-brand-navy/10 rounded-2xl p-6 md:p-8"
      data-testid="settings-pipeline-filters-card"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="inline-flex w-10 h-10 items-center justify-center bg-brand-navy text-brand-orange rounded-full">
          <Layers size={18} />
        </span>
        <div>
          <h2 className="font-semibold text-brand-navy">Pipeline Filters</h2>
          <p className="text-xs text-brand-slate/70">
            Add, rename, reorder or delete options for Plans, Statuses and Service Groups. Changes appear immediately across the Pipeline page, New Customer dialog and bulk-update tools.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-xs uppercase tracking-[0.22em] font-semibold rounded-full border transition-colors ${
              tab === t.key
                ? "bg-brand-navy text-brand-cream border-brand-navy"
                : "bg-white text-brand-navy border-brand-navy/20 hover:border-brand-navy/50"
            }`}
            data-testid={`pipeline-filters-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <ul className="divide-y divide-brand-navy/10 border border-brand-navy/10 rounded-xl overflow-hidden" data-testid={`pipeline-filters-list-${tab}`}>
        {items.map((item) => (
          <OptionRow
            key={item.slug}
            item={item}
            kind={tab}
            saving={saving}
            isDragging={draggingSlug === item.slug}
            onDragStart={() => setDraggingSlug(item.slug)}
            onDragEnd={() => setDraggingSlug(null)}
            onDrop={(toSlug) => handleDrop(draggingSlug, toSlug)}
            onRename={(label) => handleRename(item.slug, label)}
            onDelete={() => handleDelete(item.slug, item.label)}
          />
        ))}
      </ul>

      {/* Add new */}
      <AddOptionRow kind={tab} disabled={saving} onAdd={handleAdd} />
    </section>
  );
}

function OptionRow({ item, kind, saving, isDragging, onDragStart, onDragEnd, onDrop, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.label);

  const commit = async () => {
    if (label.trim() && label.trim() !== item.label) {
      const ok = await onRename(label);
      if (!ok) return;
    }
    setEditing(false);
  };

  const cancel = () => {
    setLabel(item.label);
    setEditing(false);
  };

  return (
    <li
      className={`flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-brand-cream/30 transition-colors ${
        isDragging ? "opacity-40" : ""
      }`}
      draggable={!item.locked}
      onDragStart={(e) => {
        if (item.locked) return;
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(item.slug);
      }}
      data-testid={`pipeline-filters-row-${item.slug}`}
    >
      <span className={`text-brand-slate/40 ${item.locked ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}`}>
        {item.locked ? <Lock size={14} /> : <GripVertical size={16} />}
      </span>
      {kind === "status" && (
        <Badge className={`${item.badge || "bg-slate-100 text-slate-800 border-slate-200"} rounded-full whitespace-nowrap`}>
          {item.label}
        </Badge>
      )}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") cancel();
              }}
              className="rounded-lg h-9"
              data-testid={`pipeline-filters-edit-${item.slug}`}
            />
            <button
              type="button"
              onClick={commit}
              disabled={saving}
              className="inline-flex items-center gap-1 bg-brand-orange text-white px-3 py-1.5 text-xs font-semibold rounded-full hover:bg-brand-orange-hover disabled:opacity-50"
              data-testid={`pipeline-filters-save-${item.slug}`}
            >
              <Save size={12} /> Save
            </button>
            <button
              type="button"
              onClick={cancel}
              className="text-brand-slate/60 hover:text-brand-navy p-1"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-brand-navy font-medium truncate">{item.label}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-slate/50">{item.slug}</span>
            {item.locked && <span className="text-[10px] uppercase tracking-[0.2em] text-brand-slate/50">built-in</span>}
          </div>
        )}
      </div>
      {!editing && !item.locked && (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-brand-slate/60 hover:text-brand-navy p-1"
            aria-label="Rename"
            data-testid={`pipeline-filters-rename-${item.slug}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-brand-slate/60 hover:text-red-600 p-1"
            aria-label="Delete"
            data-testid={`pipeline-filters-delete-${item.slug}`}
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
    </li>
  );
}

function AddOptionRow({ kind, disabled, onAdd }) {
  const [label, setLabel] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onAdd(label);
    if (ok) setLabel("");
  };
  return (
    <form onSubmit={submit} className="mt-4 flex items-center gap-2" data-testid={`pipeline-filters-add-${kind}`}>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={
          kind === "status"
            ? "e.g. Piloting"
            : kind === "plan"
              ? "e.g. Starter"
              : "e.g. Waste Management"
        }
        className="rounded-lg h-10 flex-1"
        data-testid={`pipeline-filters-add-input-${kind}`}
      />
      <button
        type="submit"
        disabled={disabled || !label.trim()}
        className="inline-flex items-center gap-1 bg-brand-navy text-brand-cream px-4 py-2 text-xs font-semibold rounded-full hover:bg-brand-navy/90 disabled:opacity-50"
        data-testid={`pipeline-filters-add-submit-${kind}`}
      >
        <Plus size={12} /> Add
      </button>
    </form>
  );
}
